import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useActionRequest, useDataRequest } from "../../../../../../common/hooks/api";
import { Api } from "../../../../../../common/service/Api";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { useOrganizationVendors } from "../../../../../hooks/useOrganizationVendors";
import { sortVendorsByCategory } from "../../../../../util/vendorCategorization";
import { Callout, CalloutVariant } from "../../../../Callout";
import { DataRecipientLogo } from "../../../../DataRecipientLogo";
import { CategorizedVendorList } from "../shared/CategorizedVendorList";
import { InstructionsContent } from "../shared/CollapsibleInstructions";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../../pages/get-compliant/data-recipients/shared";
import {
  getRequestProcessingRegion,
  isFacebookAds,
  isRequestContextApplicableWithCrunchFiltering,
} from "../../../../../util/vendors";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { DIRECT_MAIL_VENDORS_SLUG } from "../../../../../surveys/sharingSurvey";
import clsx from "clsx";
import { parseMultiselectAnswerWithNoneOption } from "../../../../../surveys/survey";
import { useSnackBar } from "@truevault/aquilo";
import { DataRequestCustomFieldDisplay } from "../../DataRequestCustomFieldDisplay";
import { useManagedAdNetworks } from "../../../../../../common/hooks/dataRecipientSettingsHooks";

const getAutomationCallout = (automationVendorResult: string) => {
  switch (automationVendorResult) {
    case "AUTOMATION_REQUESTED":
      return (
        <Callout
          variant={CalloutVariant.Green}
          title="Request to remove automatically sent"
        ></Callout>
      );
    case "AUTOMATION_CONFIRMED":
      return (
        <Callout variant={CalloutVariant.Green} title="Consumer automatically removed"></Callout>
      );
    case "AUTOMATION_NO_DATA":
      return (
        <Callout
          variant={CalloutVariant.Secondary}
          title="Vendor indicated no data to remove for the consumer"
        ></Callout>
      );
    default:
      return null;
  }
};

export const RemoveConsumer = () => {
  const [org] = usePrimaryOrganization();

  const [allVendors] = useOrganizationVendors(org.id, undefined, true);
  const { request, refreshRequest, setStepCompleted } = useDataRequestProcessingContext();
  const [vendorOutcomes, setVendorOutcomes] = useState<Record<string, string>>({});

  const isBatch = !!request.batch;

  const [isPerformingVendorUpdate, setIsPerformingVendorUpdate] = useState<boolean>(false);

  const snackbar = useSnackBar();
  const enqueueSnackbarElevated = useCallback(
    (message, options) => {
      snackbar(message, options);
    },
    [snackbar],
  );

  const [bulkProgress, setBulkProgress] = useState(0); // How many vendors have we gotten through updating for the bulk workflow?
  const numBulkRequests = request?.batch?.requestPublicIds?.length ?? 0; // How many vendors total are there for the bulk workflow
  const bulkProgressFormatted =
    bulkProgress > 0 && numBulkRequests > 0 ? `(${bulkProgress} of ${numBulkRequests})` : null; // Formatting for displaying the progress of the bulk workflow
  const [vendorIdInProgress, setVendorIdInProgress] = useState(null); // Which vendor is being updated (so we know for which vendor to show the loading spinner)

  const { result: fetchedInstructions } = useDataRequest({
    queryKey: ["fetchedOptOutInstructions", org.id],
    api: () => Api.requestHandlingInstructions.getOptOutInstructions(org.id),
  });
  // This does not match where the data is stored based on the OptOutInstructionPage
  // It may very well be that we want to update the OptOutInstructionPage Page instead
  // const [fetchedInstructions] = useRequestInstructions(org.id, workflowType);

  const { fetch: updateVendorOutcome } = useActionRequest({
    api: async ({ values }) => {
      setVendorIdInProgress(values.vendor);

      let requests: string[] = [];
      if (isBatch) {
        requests = request.batch.requestPublicIds;
        if (requests.length > 25) {
          enqueueSnackbarElevated(
            `Updating ${requests.length} requests may take a few moments, please wait...`,
            { variant: "info" },
          );
        }
      } else {
        requests = [request.id];
      }

      for (let i = 0; i < requests.length; i++) {
        const resp = await Api.dataRequest.updateVendor(org.id, requests[i], values);
        setBulkProgress(i);
        if (!resp.id) {
          // Don't throw here since the other requests need to finish still
          console.error(`invalid response from server for request ${requests[i]}`);
        }
      }

      if (isBatch) {
        // Some visual feedback for the batch workflow that the user is affecting a group of requests
        enqueueSnackbarElevated(`Saved update for ${request.batch.requestCount} requests`, {
          variant: "success",
        });
      }

      setVendorIdInProgress(null);
    },
    onError: (networkRequestState) => {
      if (networkRequestState.errorMessage === undefined) {
        networkRequestState.errorMessage =
          "There was a problem saving your selection. Please try again.";
      }
      setVendorIdInProgress(null);
    },
  });

  const onSelectionChange = async (vendor, value) => {
    try {
      setIsPerformingVendorUpdate(true);

      await updateVendorOutcome({
        values: { vendor: vendor.vendorId, outcome: value },
      });
      setVendorOutcomes((prev) => Object.assign({}, prev, { [vendor.vendorId]: value }));
    } catch (e) {
      console.error("Error updating vendor outcome:", e);
    } finally {
      // Always reset state to ensure checkboxes are re-enabled
      setIsPerformingVendorUpdate(false);
      refreshRequest();
    }
  };

  const { answers } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);
  const { data: dataMap } = useDataMap(org.id);
  const { managedAdNetworks } = useManagedAdNetworks(org.id);

  // Get the selected custom audience vendor managers from the survey
  const customAudienceVendorManagers = parseMultiselectAnswerWithNoneOption(
    answers,
    "custom-audience-feature-separate-vendor",
  );

  // Get managed ad networks from database
  const getManagersForNetwork = (networkId: string) => {
    return (
      managedAdNetworks
        ?.filter(
          (item) =>
            item.networkId === networkId && customAudienceVendorManagers.includes(item.managerId),
        )
        ?.map((item) => allVendors?.find((v) => v.id === item.managerId))
        ?.filter(Boolean) || []
    );
  };

  const requestProcessingRegion = getRequestProcessingRegion(request);

  // Get the first manager for a network (for backward compatibility with single-manager displays)
  const getManagementVendorForNetwork = (networkId: string) => {
    const managers = getManagersForNetwork(networkId);
    return managers[0] || null;
  };

  // Get all network IDs that are managed by someone (only if the manager is still selected)
  const managedAdNetworksIds: string[] = [
    ...new Set(
      managedAdNetworks
        ?.filter((item) => customAudienceVendorManagers.includes(item.managerId))
        ?.map((item) => item.networkId) || [],
    ),
  ];

  // Get all unique manager IDs
  const managementVendorIds: string[] = [
    ...new Set(managedAdNetworks?.map((item) => item.managerId) || []),
  ];

  // Filter management vendors to only include those that are still selected in the survey
  const managementVendors =
    allVendors?.filter(
      (v) => managementVendorIds.includes(v.id) && customAudienceVendorManagers.includes(v.id),
    ) || [];

  const directMailVendorIds: string[] = parseMultiselectAnswerWithNoneOption(
    answers,
    DIRECT_MAIL_VENDORS_SLUG,
  );

  const vendors = useMemo(() => {
    if (!allVendors) {
      return undefined;
    }

    let result = allVendors.filter((v) => {
      if (!v.processingRegions.includes(requestProcessingRegion)) {
        return false;
      }
      // Ignore inaccessible vendors
      if (v.dataAccessibility == "NOT_ACCESSIBLE") {
        return false;
      }

      if (
        !isRequestContextApplicableWithCrunchFiltering(dataMap, request.context, v, request, org)
      ) {
        return false;
      }

      // If a management vendor exists, then hide vendors that are managed by the management vendor
      if (managementVendors.length > 0 && managedAdNetworksIds.includes(v.id)) {
        return false;
      }

      return (
        v.isUploadVendor ||
        v.isAlwaysUploadVendor ||
        v.usesCustomAudience ||
        directMailVendorIds.includes(v.id)
      );
    });
    // Add all accessible management vendors that aren't already included
    const managementVendorsToAdd = managementVendors.filter((vendor) => {
      if (result.find((v) => v.id === vendor.id)) return false; // Already included
      if (vendor.dataAccessibility !== "ACCESSIBLE") return false; // Not accessible
      return managedAdNetworksIds.length > 0; // Only if there are managed networks
    });

    if (managementVendorsToAdd.length > 0) {
      result.push(...managementVendorsToAdd);
    }
    return result;
  }, [
    allVendors,
    directMailVendorIds,
    dataMap,
    request,
    managementVendors,
    managedAdNetworksIds,
    answers,
    org,
    requestProcessingRegion,
  ]);

  useEffect(() => {
    const vendorOutcomes = request.vendorOutcomes.reduce((acc, outcome) => {
      return Object.assign({}, acc, { [outcome.vendor.id]: outcome.outcome });
    }, {});

    // Only update if there are actual changes to prevent infinite loops
    setVendorOutcomes((prev) => {
      const hasChanges =
        Object.keys(vendorOutcomes).some((key) => vendorOutcomes[key] !== prev[key]) ||
        Object.keys(prev).length !== Object.keys(vendorOutcomes).length;

      return hasChanges ? vendorOutcomes : prev;
    });
  }, [request]);

  useEffect(() => {
    const outcomes = request.vendorOutcomes;
    const everyOutcomeCompleted = vendors?.every((vendor) => {
      const outcome = outcomes?.find((o) => o.vendor.id == vendor.vendorId);
      return outcome?.outcome != null && outcome?.outcome !== "AUTOMATION_FAILED";
    });

    setStepCompleted(
      vendors &&
        vendors.length <= outcomes.length &&
        everyOutcomeCompleted &&
        !isPerformingVendorUpdate,
    );
  }, [request, setStepCompleted, vendors, isPerformingVendorUpdate]);

  const sortedVendors = useMemo(() => {
    return sortVendorsByCategory(vendors ?? []);
  }, [vendors]);

  return (
    <>
      <div className="text-t3 mb-std">
        Remove the consumer from any data sharing and selling processes or lists involving the Data
        Recipients below. Your defaults and instructions can be updated in{" "}
        <Link className="text-weight-regular" to="/organization/requestHandling">
          Request Instructions.
        </Link>
      </div>

      {Boolean(fetchedInstructions) && (
        <Callout variant={CalloutVariant.Clear} className={"mb-std"}>
          <p>{fetchedInstructions}</p>
        </Callout>
      )}

      <DataRequestCustomFieldDisplay
        org={org}
        request={request}
        classList={fetchedInstructions ? "mb-std" : "mb-md"}
      />

      {sortedVendors && (
        <CategorizedVendorList
          vendors={sortedVendors}
          renderVendor={(vendor) => {
            const optOutInstruction = vendor.instructions.find((vi) => vi.requestType == "OPT_OUT");
            const resolutionValue = vendorOutcomes[vendor.vendorId] || ""; // Need to always have some value here to prevent MUI from treating `value` as uncontrolled

            const resolvedAutomatically = [
              "AUTOMATION_REQUESTED",
              "AUTOMATION_CONFIRMED",
              "AUTOMATION_NO_DATA",
            ].includes(resolutionValue);

            const reconnectNudge = (
              <>
                Try disconnecting and reconnecting to this vendor on the{" "}
                <Link className="text-weight-regular" to="/privacy-center/integrations">
                  Integrations page
                </Link>
                , then press the Automate button to retry.
              </>
            );
            const metaAdsTOSReminder = (
              <>
                For Meta Ads, be sure you've logged in to the Meta Business Account at least once
                and accepted the Custom Audience Terms of Service.
              </>
            );

            const rowContent = resolvedAutomatically ? (
              getAutomationCallout(resolutionValue)
            ) : (
              <>
                {resolutionValue === "AUTOMATION_FAILED" && (
                  <Callout
                    variant={CalloutVariant.Yellow}
                    title="Automatic removal not successful"
                    className="mb-sm"
                    subtitle={
                      isFacebookAds(vendor) ? (
                        <>
                          {reconnectNudge} {metaAdsTOSReminder}
                        </>
                      ) : (
                        <>{reconnectNudge}</>
                      )
                    }
                  ></Callout>
                )}
                <FormControl component="fieldset">
                  <FormControlLabel
                    control={
                      <Checkbox
                        aria-label="Vendor handling response"
                        name={`vendor-${vendor.id}-data-handling`}
                        onChange={(e) => {
                          onSelectionChange(vendor, e.target.checked ? "COMPLETE" : null);
                        }}
                        checked={resolutionValue === "COMPLETE"}
                        disabled={isPerformingVendorUpdate}
                      />
                    }
                    label={
                      isPerformingVendorUpdate &&
                      vendorIdInProgress == vendor.vendorId &&
                      numBulkRequests > 1 ? (
                        <div className="flex-row">
                          <span>Completed</span>
                          <CircularProgress
                            color="primary"
                            size="1rem"
                            className="ml-xs"
                            sx={{ marginTop: "4px" }}
                          />
                          <Typography variant="subtitle2">{bulkProgressFormatted}</Typography>
                        </div>
                      ) : (
                        "Completed"
                      )
                    }
                  />
                </FormControl>
              </>
            );

            return (
              <div key={`vendor-${vendor.id}`} className={"vendor-data-container"}>
                <hr />
                <div className={"flex-container pt-std pb-std border-top--grey vendor-data-card"}>
                  <div className="flex-grow vendor-data-vanity">
                    <div
                      className={clsx(
                        "text-t4",
                        "w-256",
                        "flex-container",
                        "flex--align-center",
                        "pl-xs",
                        resolvedAutomatically ? "mt-sm" : null,
                      )}
                    >
                      <DataRecipientLogo dataRecipient={vendor} size={32} />
                      <div className="ml-mdlg" style={{ width: "95px" }}>
                        {vendor.name}
                        {managedAdNetworksIds.includes(vendor.id) && (
                          <span className="text-neutral-600 ml-xs flex-row">
                            (managed by {getManagementVendorForNetwork(vendor.id)?.name})
                          </span>
                        )}
                      </div>
                      {resolvedAutomatically && (
                        <img
                          src="/assets/images/automation-icon.svg"
                          alt="Resolved automatically"
                          className="ml-xs"
                        />
                      )}
                    </div>
                    {(() => {
                      // Show which networks this vendor manages (only if they are an active manager)
                      const networksManaged =
                        managedAdNetworks
                          ?.filter(
                            (item) =>
                              item.managerId === vendor.id &&
                              customAudienceVendorManagers.includes(item.managerId),
                          )
                          ?.map((item) => allVendors?.find((v) => v.id === item.networkId))
                          ?.filter(Boolean) || [];

                      if (networksManaged.length === 0) return null;

                      return (
                        <div className="pl-xs">
                          <div className="text-weight-bold mt-lg text-t1">
                            Vendors managed by {vendor.name}:
                          </div>
                          {networksManaged.map((network) => (
                            <div className="managing-vendor-subtext text-t1" key={network?.id}>
                              <DataRecipientLogo dataRecipient={network} size={32} />
                              <span> {network?.name} </span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex-grow">
                    {rowContent}

                    {!resolvedAutomatically &&
                      (() => {
                        const isManaged = managedAdNetworksIds.includes(vendor.id);
                        const manager = isManaged ? getManagementVendorForNetwork(vendor.id) : null;
                        const instructionsToShow =
                          isManaged && manager
                            ? manager.optOutInstructions
                            : vendor.optOutInstructions;

                        if (!instructionsToShow && !isManaged) return null;

                        return (
                          <Callout variant={CalloutVariant.Gray} className={"my-md"}>
                            <div className="text-weight-bold">
                              {isManaged && manager
                                ? `Opt-Out Instructions through ${manager.name}`
                                : `Opt-Out Instructions`}
                            </div>
                            <div
                              className="text-t2"
                              dangerouslySetInnerHTML={{
                                __html:
                                  instructionsToShow ||
                                  "We have not found opt-out instructions for this vendor. Contact the vendor for instructions on how to remove the consumer from a custom audience list.",
                              }}
                            ></div>
                          </Callout>
                        );
                      })()}

                    {!resolvedAutomatically && (
                      <InstructionsContent
                        processingInstructions={optOutInstruction?.processingInstructions}
                        title="Opt-out Instructions"
                        isExpanded={true}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          }}
        />
      )}
    </>
  );
};
